import React, { useState, useCallback } from 'react';
import { useLocationSelector } from './useLocationSelector';
import { LocationSelectorDialog } from './LocationSelectorDialog';
import { createVenue } from '../../../api/administration/demographics/locations';

const INPUT_BASE_CLASS =
  'w-full rounded-lg px-2 py-1.5 text-xs text-slate-700 border border-gray-300 bg-surface-muted shadow-sm focus:border-primary-dark focus:outline-none focus:ring-1 focus:ring-primary-dark/40 transition disabled:cursor-not-allowed disabled:opacity-70 disabled:bg-gray-100';

/**
 * Resolves display text from value. Shows location hierarchy as "Country > Zone > Region > District > Ward > Venue".
 * @param {Object|string} value - { location, displayText, country, zone, ... } or string
 * @returns {string}
 */
function getDisplayValue(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value.displayText) return value.displayText;
  if (typeof value === 'object' && value.location) {
    return value.location.name ?? value.location.title ?? '';
  }
  return '';
}

/**
 * Location selector: label, read-only field, and Select button.
 * Clicking Select opens a dialog for hierarchical location selection.
 * Selection stops at the specified level (countryLevel, zoneLevel, regionLevel, districtLevel, wardLevel, venueLevel),
 * or can allow any depth with locationLevel.
 *
 * @param {Object} props
 * @param {string} props.id - Input id
 * @param {string} [props.label] - Label
 * @param {boolean} [props.required]
 * @param {string} [props.error]
 * @param {Object|string} props.value - Current selection: { location, displayText, country, zone, ... } or string
 * @param {function(Object|null): void} props.onChange - Called with { location, country, zone, region, district, ward, venue, displayText } or null
 * @param {string} [props.placeholder] - Placeholder when nothing selected
 * @param {number} [props.labelWidthRem]
 * @param {'top'|'left'} [props.labelPosition]
 * @param {boolean} [props.disabled]
 * @param {'countryLevel'|'zoneLevel'|'regionLevel'|'districtLevel'|'wardLevel'|'venueLevel'|'locationLevel'} [props.level='venueLevel'] - Level at which selection stops
 */
export function LocationSelector({
  id,
  label,
  required = false,
  error,
  value = '',
  onChange = () => {},
  placeholder = 'Select location...',
  labelWidthRem = 8,
  labelPosition = 'top',
  disabled = false,
  level = 'venueLevel',
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const {
    loading,
    countries,
    zones,
    regions,
    districts,
    wards,
    venues,
    refetchVenues,
    selectedCountry,
    selectedZone,
    selectedRegion,
    selectedDistrict,
    selectedWard,
    selectedVenue,
    setSelectedCountry,
    setSelectedZone,
    setSelectedRegion,
    setSelectedDistrict,
    setSelectedWard,
    setSelectedVenue,
    applyVenueSelection,
    resetSelections,
  } = useLocationSelector(level);

  const handleAddVenue = useCallback(
    async (payload) => {
      const res = await createVenue(payload);
      await refetchVenues?.();
      return res?.data?.venue ?? res?.venue ?? res ?? null;
    },
    [refetchVenues]
  );

  const displayValue = getDisplayValue(value);
  const errorId = id ? `${id}-error` : undefined;
  const isTop = labelPosition === 'top';

  const labelEl = label ? (
    <label htmlFor={id} className="text-sm font-medium text-primary-dark">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
  ) : null;

  const fieldContent = (
    <div className="min-w-0">
      <div className="flex gap-0 rounded border border-gray-300 bg-white overflow-hidden">
        <input
          id={id}
          type="text"
          readOnly
          disabled={disabled}
          value={displayValue}
          placeholder={placeholder}
          className={`${INPUT_BASE_CLASS} flex-1 min-w-0 rounded-none border-0 border-r border-gray-300`}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
        />
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => !disabled && !loading && setDialogOpen(true)}
            disabled={disabled || loading}
            className="h-full px-3 py-1.5 text-xs font-medium text-slate-700 bg-gray-100 border-l border-gray-300 hover:bg-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-dark/40 focus:ring-inset disabled:opacity-60 disabled:cursor-not-allowed"
            aria-haspopup="dialog"
            aria-label="Select location"
          >
            Select
          </button>
        </div>
      </div>
      {error && (
        <p id={errorId} className="mt-1 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );

  return (
    <>
      {isTop ? (
        <div className="space-y-1">
          {labelEl}
          {fieldContent}
        </div>
      ) : (
        <div
          className="grid items-center gap-y-1"
          style={{ gridTemplateColumns: `${labelWidthRem}rem 1fr` }}
        >
          {labelEl ?? <div />}
          {fieldContent}
        </div>
      )}

      <LocationSelectorDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSelect={(selection) => {
          if (selection) onChange(selection);
          setDialogOpen(false);
        }}
        level={level}
        countries={countries}
        zones={zones}
        regions={regions}
        districts={districts}
        wards={wards}
        venues={venues}
        selectedCountry={selectedCountry}
        selectedZone={selectedZone}
        selectedRegion={selectedRegion}
        selectedDistrict={selectedDistrict}
        selectedWard={selectedWard}
        selectedVenue={selectedVenue}
        setSelectedCountry={setSelectedCountry}
        setSelectedZone={setSelectedZone}
        setSelectedRegion={setSelectedRegion}
        setSelectedDistrict={setSelectedDistrict}
        setSelectedWard={setSelectedWard}
        setSelectedVenue={setSelectedVenue}
        applyVenueSelection={applyVenueSelection}
        resetSelections={resetSelections}
        loading={loading}
        onAddVenue={['venueLevel', 'locationLevel'].includes(level) ? handleAddVenue : undefined}
      />
    </>
  );
}

export default LocationSelector;
